import type {
  EvaluateResult,
  EvaluateSummaryV2,
  EvaluateSummaryV3,
  EvaluateTable,
  EvaluateTableOutput,
  EvaluateTableRow,
  GradingResult,
} from '@promptfoo/types';

import { z } from 'zod';

import {
  getResultSearchValidationError,
  RESULT_SEARCH_MIN_LENGTH_MESSAGE,
} from '../utils/resultSearch';
import { PersistedScoreSchema } from './scores';

export const ResultSearchQuerySchema = z
  .string()
  .max(1000, { message: 'Search queries must be 1,000 characters or fewer.' })
  .refine((query) => getResultSearchValidationError(query) === null, {
    message: RESULT_SEARCH_MIN_LENGTH_MESSAGE,
  });

/**
 * Local schemas to avoid cross-Zod-instance issues.
 *
 * These schemas are simplified versions of the promptfoo schemas.
 * Using promptfoo's Zod schemas directly causes issues because they use
 * a different Zod instance, which can lead to runtime validation failures
 * in CI environments where module resolution differs.
 */

/**
 * Local ProviderOptionsSchema
 * Based on @promptfoo/validators/providers.ProviderOptionsSchema
 */
const ProviderOptionsSchema = z.object({
  id: z.string().optional(),
  label: z.string().optional(),
  config: z.any().optional(),
  prompts: z.array(z.string()).optional(),
  transform: z.string().optional(),
  delay: z.number().optional(),
  env: z.any().optional(),
  inputs: z.any().optional(),
});

/**
 * Local ProviderResponseSchema
 * Based on @promptfoo/validators/providers.ProviderResponseSchema
 */
const ProviderResponseSchema = z.object({
  cached: z.boolean().optional(),
  cost: z.number().optional(),
  incurredCost: z.number().optional(),
  error: z.string().optional(),
  logProbs: z.array(z.number()).optional(),
  metadata: z
    .object({
      redteamFinalPrompt: z.string().optional(),
    })
    .catchall(z.any())
    .optional(),
  output: z.union([z.string(), z.any()]).optional(),
  tokenUsage: z.any().optional(),
});

/**
 * Local AtomicTestCaseSchema to avoid importing from @promptfoo/types.
 * The @promptfoo/types module's TestCaseSchema references ApiProviderSchema
 * which contains z.function() - unsupported by zod-to-openapi.
 *
 * Chain: AtomicTestCaseSchema → TestCaseSchema → ApiProviderSchema → z.function()
 */
const AtomicTestCaseSchema = z.object({
  vars: z.record(z.string(), z.union([z.string(), z.any()])).optional(),
  assert: z.array(z.any()).optional(),
  threshold: z.number().optional(),
  metadata: z.record(z.string(), z.any()).optional(),
  options: z.any().optional(),
  description: z.string().optional(),
});

/**
 * Schema for eval IDs in route parameters.
 *
 * Supports both formats:
 * - UUIDs (new format): "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
 * - Legacy CLI format: "eval-XlY-2025-12-02T05:53:06"
 */
const EvalIdSchema = z.union([z.uuid(), z.string().startsWith('eval-').max(100)]);

export const EvalIdParamSchema = z.object({
  id: EvalIdSchema,
});

export type EvalIdParam = z.infer<typeof EvalIdParamSchema>;

export const ResultIdParamSchema = z.object({
  id: EvalIdSchema,
  resultsId: z.uuid('Invalid result ID format'),
});

export type ResultIdParam = z.infer<typeof ResultIdParamSchema>;

/** Rating endpoints also support opaque IDs preserved by evaluation imports. */
export const RatingIdParamSchema = z.object({
  id: z.string().min(1).describe('Stored evaluation ID, including custom imported IDs.'),
  resultsId: z.string().min(1).describe('Stored result ID, including opaque imported IDs.'),
});

export const EvalResultIdParamSchema = z.object({
  id: z.uuid('Invalid eval result ID format'),
});

export type EvalResultIdParam = z.infer<typeof EvalResultIdParamSchema>;

export const UpdateEvalSchema = z.object({
  config: z.any().optional(),
  isPublic: z.boolean().optional(),
});

export type UpdateEvalRequest = z.infer<typeof UpdateEvalSchema>;

export const ResultsSchema = z.array(
  z.object({
    id: z.string().optional(),
    prompt: z.any(),
    provider: z.any(),
    vars: z.record(z.string(), z.any()).optional(),
    output: z.any(),
    latencyMs: z.number().optional(),
    success: z.boolean().optional(),
    error: z.string().optional().nullable(),
    gradingResult: z.any().optional(),
  }),
);

export type ResultsRequest = z.infer<typeof ResultsSchema>;

export const DEFAULT_EVAL_SORT_FIELD = 'createdAt' as const;
export const DEFAULT_EVAL_SORT_DIRECTION = 'desc' as const;

export const EvalSortFieldSchema = z.enum([
  'createdAt',
  'evalId',
  'description',
  'author',
  'configName',
  'targets',
  'pluginIds',
  'strategies',
  'passRate',
  'numTests',
  'durationMs',
]);

export type EvalSortField = z.infer<typeof EvalSortFieldSchema>;

export const EvalSortDirectionSchema = z.enum(['asc', 'desc']);

export type EvalSortDirection = z.infer<typeof EvalSortDirectionSchema>;

export const ReportExportQuerySchema = z.object({
  teamId: z.string().min(1),
  sortField: EvalSortFieldSchema.extract([
    'createdAt',
    'evalId',
    'description',
    'targets',
  ]).prefault(DEFAULT_EVAL_SORT_FIELD),
  sortDirection: EvalSortDirectionSchema.prefault(DEFAULT_EVAL_SORT_DIRECTION),
});

export const ReportExportRowSchema = z.object({
  evalId: z.string(),
  createdAt: z.string(),
  description: z.string().nullable(),
  target: z.string().nullable(),
});
export type ReportExportRow = z.infer<typeof ReportExportRowSchema>;

export const ListEvaluationsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).prefault(50),
  offset: z.coerce.number().int().min(0).prefault(0),
  datasetId: z.string().optional(),
  configId: z.string().optional(),
  isRedteam: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
  sortField: EvalSortFieldSchema.prefault(DEFAULT_EVAL_SORT_FIELD),
  sortDirection: EvalSortDirectionSchema.prefault(DEFAULT_EVAL_SORT_DIRECTION),
});

export type ListEvaluationsQuery = z.infer<typeof ListEvaluationsQuerySchema>;

export const GradeResultSchema = z.object({
  result: z.string(),
  score: z.number().min(0).max(1).optional(),
  reasonsHtml: z.string().optional(),
  reasons: z.array(z.string()).optional(),
});

export type GradeResultRequest = z.infer<typeof GradeResultSchema>;

export const TRUE_RATING_ASSERTION_VALUES = new Set([
  '1',
  't',
  'tr',
  'tru',
  'true',
  'y',
  'ye',
  'yes',
  'on',
]);
export const FALSE_RATING_ASSERTION_VALUES = new Set([
  '0',
  'f',
  'fa',
  'fal',
  'fals',
  'false',
  'n',
  'no',
  'of',
  'off',
]);
export const RATING_ASSERTION_WHITESPACE = ' \t\n\r';
// Bound guidance-marker inspection, not stored JSON or ordinary assertion evaluation.
// Larger guidance histories use the stored row verdict in both JavaScript and SQL.
export const MAX_GUIDANCE_REVIEW_COMPONENTS = 1000;

const legacyRatingAssertionWords = [
  ...TRUE_RATING_ASSERTION_VALUES,
  ...FALSE_RATING_ASSERTION_VALUES,
]
  .map((word) => word.replace(/[a-z]/g, (letter) => `[${letter}${letter.toUpperCase()}]`))
  .join('|');

export const RATING_ASSERTION_LEGACY_STRING_PATTERN = `^[ \\t\\n\\r]*(?:${legacyRatingAssertionWords})[ \\t\\n\\r]*(?![\\s\\S])`;

/** Parses component verdicts recognized by stored metrics and preserved by JSON storage. */
export function parseRatingAssertionVerdict(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value === 1 ? true : value === 0 ? false : undefined;
  }
  if (typeof value !== 'string') {
    return undefined;
  }

  let start = 0;
  let end = value.length;
  while (start < end && RATING_ASSERTION_WHITESPACE.includes(value[start])) {
    start++;
  }
  while (end > start && RATING_ASSERTION_WHITESPACE.includes(value[end - 1])) {
    end--;
  }
  const normalized = value.slice(start, end).toLowerCase();
  if (TRUE_RATING_ASSERTION_VALUES.has(normalized)) {
    return true;
  }
  if (FALSE_RATING_ASSERTION_VALUES.has(normalized)) {
    return false;
  }
  return undefined;
}

export const GradingRevisionSchema = z
  .string()
  .regex(/^[a-f0-9]{64}$/)
  .describe('Opaque revision for the stored grading state of an individual evaluation result.');

export const MAX_RATING_JUSTIFICATION_LENGTH = 4000;

/** Require visible content without stripping joiners or formatting from a reviewer's text. */
export function hasRatingJustificationContent(value: string): boolean {
  return /[^\p{White_Space}\p{Default_Ignorable_Code_Point}\p{C}\u2800]/u.test(value);
}

const missingRatingJustification = 'Enter a justification for this grade change.';
const RatingJustificationSchema = z
  .string()
  .trim()
  .min(1, { error: missingRatingJustification, abort: true })
  .max(MAX_RATING_JUSTIFICATION_LENGTH)
  .refine(hasRatingJustificationContent, missingRatingJustification)
  .describe(
    'Explanation for this grade change. Required when a human changes a red team result from Vulnerable to Safe. Stored with the change, separately from the editable result comment.',
  );

export const RATING_CONFLICT_CODE = 'rating_conflict';
export const RATING_CONFLICT_MESSAGE = 'The rating has changed. Refresh the result and try again.';

export const RatingConflictResponseSchema = z.object({
  error: z.literal(RATING_CONFLICT_MESSAGE),
  code: z.literal(RATING_CONFLICT_CODE),
});

export const ManualRatingQuerySchema = z.object({
  expectedGradingRevision: GradingRevisionSchema.optional().describe(
    'When supplied, the rating is saved only if this still matches the gradingRevision from the table output or last successful rating response. A stale revision returns 409 without updating the result, prompt metrics, or associated issue. Omit only to use unconditional replacement.',
  ),
  preservePublicGraderExamples: z
    .enum(['true', 'false'])
    .optional()
    .describe(
      'Set to true when resubmitting a public evaluation table to preserve omitted inline grader examples on otherwise unchanged assertion entries, even if the evaluation is now private. A uniquely identifiable manual assertion can also change its rating fields. Explicitly supplied examples are handled as part of the replacement. Ambiguous partial matches are rejected.',
    ),
});

export const ManualRatingRequestSchema = z
  .looseObject({
    pass: z.boolean().describe('Whether the result should be marked as passing'),
    score: PersistedScoreSchema.describe('Numeric score to store for the result'),
    reason: z
      .unknown()
      .default('')
      .describe('Human grading reason; legacy structured JSON is stored as provided'),
    comment: z.string().optional().describe('Optional grading comment'),
    // Component verdicts must be checked against stored historical assertions after parsing the body.
    componentResults: z
      .array(z.unknown())
      .optional()
      .describe(
        'Submitted assertion entries use standard JSON storage normalization. New or changed non-null verdicts must be booleans, supported legacy boolean strings, or 0 or 1. Legacy strings allow mixed letter case and surrounding spaces, tabs, or line breaks. Previously stored assertions with an unsupported verdict may be resent unchanged after storage normalization, up to their stored number of occurrences. For a private evaluation table obtained with authorized access, resolve generated graderExamplesId references to inline graderExamples from the same response. Any already stored graderExamplesId value, including a non-string value, may instead be resent as part of an unchanged assertion and only as many times as it was stored; no new graderExamplesId value is stored. A request with expectedGradingRevision may also edit pass, score, reason, or comment on a uniquely identified human assertion with a stored reference; its submitted verdict must be supported, and its reference and other assertion data must remain unchanged. Older clients that omit expectedGradingRevision may echo a generated reference only when the otherwise unchanged assertion uniquely matches stored inline examples, up to the stored occurrence count; ambiguous matches must be resolved by the client. Public evaluation tables omit inline grader examples, do not generate references, and return an empty registry even for signed-in clients; previously stored references may remain unresolved. Use preservePublicGraderExamples=true to preserve omitted examples on otherwise unchanged public-table entries, or use complete original grading data you already possess. If omitted, existing entries are removed.',
      ),
  })
  .describe(
    'Replacement grading result. Only submitted fields are retained; an omitted reason defaults to an empty string. Additional submitted fields are accepted.',
  );

export type ManualRatingRequest = z.infer<typeof ManualRatingRequestSchema>;

/** The original loose replacement body may already contain arbitrary importer-owned fields. */
export const ReviewedManualRatingRequestSchema = z.strictObject({
  gradingResult: ManualRatingRequestSchema,
  justification: RatingJustificationSchema,
});

const LegacyManualRatingSubmissionSchema = ManualRatingRequestSchema.transform((gradingResult) => ({
  gradingResult,
  justification: undefined,
}));

/** Select the wire format before validation so errors still point to the caller's fields. */
export function safeParseManualRatingSubmission(input: unknown) {
  const reviewed =
    typeof input === 'object' &&
    input !== null &&
    Object.prototype.hasOwnProperty.call(input, 'gradingResult') &&
    !Object.prototype.hasOwnProperty.call(input, 'pass') &&
    !Object.prototype.hasOwnProperty.call(input, 'score');
  return reviewed
    ? ReviewedManualRatingRequestSchema.safeParse(input)
    : LegacyManualRatingSubmissionSchema.safeParse(input);
}

/** Historical grading objects retain opaque fields; consumers must narrow them before reading. */
export const LegacyGradingResultSchema = z.looseObject({});
export type LegacyGradingResult = { [Field in keyof GradingResult]?: unknown } & Record<
  string,
  unknown
>;

/** Imported grading may also contain a primitive or array at its root. */
export const LegacyGradingValueSchema = z.union([
  LegacyGradingResultSchema,
  z.array(z.unknown()),
  z.string(),
  z.number(),
  z.boolean(),
]);
export type LegacyGradingValue = z.infer<typeof LegacyGradingValueSchema>;

/** Persisted/imported evaluation projections retain opaque grading JSON. */
export type LegacyEvaluateResult = Omit<EvaluateResult, 'gradingResult'> & {
  gradingResult?: LegacyGradingValue | null;
  /** Indexed category used by log filters; omitted when no persisted result row was projected. */
  persistedPluginId?: string | null;
};
export type LegacyEvaluateTableOutput = Omit<EvaluateTableOutput, 'gradingResult'> & {
  gradingResult?: LegacyGradingValue | null;
};
export type LegacyEvaluateTableRow = Omit<EvaluateTableRow, 'outputs'> & {
  /** Missing provider results retain their column position as null in JSON. */
  outputs: (LegacyEvaluateTableOutput | null)[];
};
export type LegacyEvaluateTable = Omit<EvaluateTable, 'body'> & { body: LegacyEvaluateTableRow[] };
export type LegacyEvaluateSummaryV3 = Omit<EvaluateSummaryV3, 'results'> & {
  results: LegacyEvaluateResult[];
};
export type LegacyEvaluateSummaryV2 = Omit<EvaluateSummaryV2, 'results' | 'table'> & {
  results: LegacyEvaluateResult[];
  table: LegacyEvaluateTable;
};

/** Changes are merged with the stored rating after checking its revision under a row lock. */
export const PatchRatingRequestSchema = z
  .strictObject({
    expectedGradingRevision: GradingRevisionSchema,
    pass: z
      .boolean()
      .nullable()
      .optional()
      .describe(
        'Set a manual verdict, defaulting an omitted score to 1 for pass or 0 for fail and recording the reviewer. Null removes all human overrides and recomputes from automatic assertions.',
      ),
    score: PersistedScoreSchema.optional().describe(
      'Set a manual score, preserving the current verdict unless pass is supplied.',
    ),
    comment: z
      .string()
      .optional()
      .describe('Replace the comment; an empty string clears it. Omission preserves it.'),
    justification: RatingJustificationSchema.optional(),
  })
  .refine(
    (edit) => edit.pass !== undefined || edit.score !== undefined || edit.comment !== undefined,
    {
      message: 'Supply pass, score, or comment to edit.',
    },
  )
  .refine((edit) => edit.pass !== null || edit.score === undefined, {
    message: 'A score cannot be supplied when removing the manual override.',
    path: ['score'],
  });

export type PatchRatingRequest = z.infer<typeof PatchRatingRequestSchema>;

/** JSON response shared by direct rating reads and conditional edits. */
export const RatingResponseSchema = z.object({
  id: z.string(),
  evalId: z.string(),
  success: z.boolean(),
  score: z.number(),
  gradingResult: LegacyGradingValueSchema.nullable(),
  gradingRevision: GradingRevisionSchema,
  gradingHistory: z
    .array(z.unknown())
    .optional()
    .describe('Previous grading outcomes in chronological order.'),
  updatedAt: z.iso.datetime(),
  failureReason: z
    .union([z.literal(0), z.literal(1), z.literal(2)])
    .describe('0: no known failure; 1: assertion failure; 2: execution error.'),
});

export type RatingResponseDTO = z.infer<typeof RatingResponseSchema>;

/** Serialized legacy replacement response; all model fields are selected explicitly. */
export const EvalResultResponseSchema = RatingResponseSchema.extend({
  description: z.string().nullable().optional(),
  testIdx: z.number(),
  promptIdx: z.number(),
  namedScores: z.record(z.string(), z.number()),
  testCase: z.unknown(),
  prompt: z.unknown(),
  promptId: z.string(),
  provider: z.unknown(),
  response: z.unknown(),
  error: z.string().nullable().optional(),
  latencyMs: z.number(),
  cost: z.number(),
  persisted: z.boolean(),
  createdAt: z.iso.datetime(),
  metadata: z.record(z.string(), z.unknown()),
  pluginId: z.string().nullable(),
  strategyId: z.string().nullable(),
});

export type EvalResultResponseDTO = z.infer<typeof EvalResultResponseSchema>;

const resultsCommentError = 'reason is required and must be a string';

export const ResultsCommentRequestSchema = z.object(
  {
    reason: z
      .string({ error: resultsCommentError })
      .min(1, { error: resultsCommentError })
      .describe('The grading reason or comment to append'),
  },
  { error: resultsCommentError },
);

const bulkRatingIdsError = 'resultIds must be a non-empty array';

export const BulkRatingRequestSchema = z.object(
  {
    resultIds: z
      .array(z.string({ error: 'resultIds must contain only strings' }), {
        error: bulkRatingIdsError,
      })
      .min(1, { error: bulkRatingIdsError })
      .describe('Array of result IDs to update'),
    pass: z.boolean({ error: 'pass must be a boolean' }).describe('Whether the results passed'),
    reason: z
      .string({ error: 'reason must be a string' })
      .optional()
      .describe('Optional rating reason. If omitted or empty, a default reason is used.'),
    justification: RatingJustificationSchema.optional(),
  },
  { error: bulkRatingIdsError },
);

export type BulkRatingRequest = z.infer<typeof BulkRatingRequestSchema>;

export const GradeResultIdSchema = z.object({
  evalResultId: z.uuid(),
});

export type GradeResultIdRequest = z.infer<typeof GradeResultIdSchema>;

const ISO_8601_WITH_TIMEZONE_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

/**
 * Public API input for evaluation creation timestamps.
 *
 * The wire contract accepts:
 * - Legacy numeric timestamps (milliseconds since Unix epoch)
 * - ISO 8601 strings with explicit timezone information
 */
const StrictDateInputSchema = z.union([
  z.number().int(),
  z.string().regex(ISO_8601_WITH_TIMEZONE_REGEX),
]);

/**
 * Strict date coercion that accepts:
 * - Date instances
 * - Numeric timestamps (milliseconds since Unix epoch)
 * - ISO 8601 strings with timezone (e.g., "2025-01-15T10:30:00Z" or "2025-01-15T10:30:00+00:00")
 *
 * Rejects null and timezone-less strings to avoid silent coercion to unexpected values.
 */
const StrictDateSchema = z.preprocess(
  (input) => (input instanceof Date ? input.toISOString() : input),
  StrictDateInputSchema.transform((input) => new Date(input)).pipe(
    z.date().refine((d) => Number.isFinite(d.getTime()), { message: 'Invalid date value' }),
  ),
);

export const CreateEvalSchema = z.object({
  id: z
    .string()
    .min(1)
    .optional()
    .describe(
      'External evaluation ID. Omit to generate an ID. Replacing an existing evaluation requires update and delete permission for its team.',
    ),
  // Strict date validation for backward compatibility
  // Accepts Date instances, numeric timestamps, or ISO strings with explicit timezone
  createdAt: StrictDateSchema,
  vars: z.array(z.string()).optional().nullable(),
  config: z
    .object({
      metadata: z
        .object({
          teamId: z.uuid().optional(),
        })
        .optional(),
    })
    .optional(),
  // Wall-clock duration fields (milliseconds), sent by CLI when sharing evals.
  durationMs: z.number().int().nonnegative().optional().nullable(),
  generationDurationMs: z.number().int().nonnegative().optional().nullable(),
  evaluationDurationMs: z.number().int().nonnegative().optional().nullable(),
});

export type CreateEvalRequest = z.infer<typeof CreateEvalSchema>;

export const ShareEvalRequestSchema = z.object({
  roleId: z.uuid('Invalid role ID format').optional(),
});

export type ShareEvalRequest = z.infer<typeof ShareEvalRequestSchema>;

export const ShareEvalResponseSchema = z.object({
  link: z.string(),
  expiresAt: z.iso
    .datetime()
    .optional()
    .describe(
      'Present only when the link is an invite that adds whoever registers with it to the organization. Organization admins receive invite links; other members receive a plain link to the evaluation.',
    ),
});

export type ShareEvalResponse = z.infer<typeof ShareEvalResponseSchema>;

export const RevokeEvalShareLinksResponseSchema = z.object({
  revoked: z.number().int().nonnegative(),
});

export type RevokeEvalShareLinksResponse = z.infer<typeof RevokeEvalShareLinksResponseSchema>;

export const CsvExportFieldsQuerySchema = z.union([z.string(), z.array(z.string())]).optional();

export const FetchResultQueryParamsSchema = z
  .object({
    format: z.enum(['csv', 'json', 'xlsx']).optional(),
    teamId: z.string().optional(), // Optional team scope for CLI team-context checks
    fields: CsvExportFieldsQuerySchema,
    offset: z.coerce.number().int().min(0).optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    includeTraces: z
      .enum(['true', 'false'])
      .optional()
      .transform((value) => value !== 'false'),
    refreshMetrics: z
      .enum(['true', 'false'])
      .optional()
      .transform((value) => value !== 'false'),
  })
  .refine(({ offset, limit }) => offset === undefined || limit !== undefined, {
    message: 'offset requires limit',
    path: ['limit'],
  });

export type FetchResultQueryParams = z.infer<typeof FetchResultQueryParamsSchema>;

export const ReportResultIdsQuerySchema = z.object({ teamId: z.string().optional() });

/** Null identifies embedded legacy results, which are returned in a single response. */
export const ReportResultIdsResponseSchema = z.object({
  resultIds: z.array(z.string()).nullable(),
});

export const BulkDeleteEvalsSchema = z.object({
  ids: z.array(z.string()).min(1).max(100), // Limit to 100 to prevent abuse
});

export type BulkDeleteEvalsRequest = z.infer<typeof BulkDeleteEvalsSchema>;

export const BulkDeleteEvalsResponseSchema = z.object({
  deletedCount: z.number(),
  failedDeletes: z.array(
    z.object({
      id: z.string(),
      reason: z.string(),
    }),
  ),
  error: z.string().nullable(),
});

export type BulkDeleteEvalsResponse = z.infer<typeof BulkDeleteEvalsResponseSchema>;

export const CopyEvalRequestSchema = z.object({
  description: z.string().optional(),
});

export type CopyEvalRequest = z.infer<typeof CopyEvalRequestSchema>;

export const CopyEvalResponseSchema = z.object({
  id: z.string(),
  distinctTestCount: z.number().optional(),
});

export type CopyEvalResponse = z.infer<typeof CopyEvalResponseSchema>;

/**
 * Token usage metrics for a prompt
 */
const CompletionTokenDetailsSchema = z.object({
  reasoning: z.number().optional(),
  acceptedPrediction: z.number().optional(),
  rejectedPrediction: z.number().optional(),
  cacheReadInputTokens: z.number().optional(),
  cacheCreationInputTokens: z.number().optional(),
});

const TokenUsageCategorySchema = z.object({
  total: z.number().optional(),
  prompt: z.number().optional(),
  completion: z.number().optional(),
  cached: z.number().optional(),
  numRequests: z.number().optional(),
  completionDetails: CompletionTokenDetailsSchema.optional(),
});

export type TokenUsageCategory = z.infer<typeof TokenUsageCategorySchema>;

const IncurredTokenUsageSchema = TokenUsageCategorySchema.extend({
  assertions: TokenUsageCategorySchema.optional(),
  attacker: TokenUsageCategorySchema.optional(),
  generation: TokenUsageCategorySchema.optional(),
});

export const TokenUsageSchema = z.object({
  total: z.number(),
  prompt: z.number(),
  completion: z.number(),
  cached: z.number(),
  numRequests: z.number(),
  completionDetails: CompletionTokenDetailsSchema.optional(),
  assertions: TokenUsageCategorySchema.optional(),
  attacker: TokenUsageCategorySchema.optional(),
  generation: TokenUsageCategorySchema.optional(),
  incurredTokenUsage: IncurredTokenUsageSchema.optional(),
});

export type TokenUsage = z.infer<typeof TokenUsageSchema>;

/**
 * Aggregated metrics for a single prompt across all filtered test cases
 */
export const PromptMetricsSchema = z.object({
  score: z.number(),
  testPassCount: z.number(),
  testFailCount: z.number(),
  testErrorCount: z.number(),
  assertPassCount: z.number(),
  assertFailCount: z.number(),
  totalLatencyMs: z.number(),
  cost: z.number(),
  incurredCost: z.number().optional(),
  tokenUsage: TokenUsageSchema,
  namedScores: z.record(z.string(), z.number()),
  namedScoresCount: z.record(z.string(), z.number()),
  namedScoreWeights: z.record(z.string(), z.number()).optional(),
});

export type PromptMetrics = z.infer<typeof PromptMetricsSchema>;

/**
 * Query parameters for fetching table data with filters
 */
export const FetchTableQueryParamsSchema = z.object({
  offset: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  filterMode: z.string().optional(),
  searchQuery: z.string().optional(),
  filters: z.union([z.string(), z.array(z.string())]).optional(),
});

export type FetchTableQueryParams = z.infer<typeof FetchTableQueryParamsSchema>;

/**
 * Response for eval table endpoint with optional filtered metrics
 * When filters are active, filteredMetrics contains metrics calculated only for the filtered dataset
 */
/**
 * Evaluation-level statistics returned by the table endpoint.
 */
export const EvalStatsSchema = z.object({
  successes: z.number(),
  failures: z.number(),
  errors: z.number(),
  tokenUsage: z.any(),
  durationMs: z.number().optional(),
  generationDurationMs: z.number().optional(),
  evaluationDurationMs: z.number().optional(),
});

export type EvalStats = z.infer<typeof EvalStatsSchema>;

export const TableGraderExamplesRegistrySchema = z.record(z.string(), z.array(z.any()));

export type TableGraderExamplesRegistry = z.infer<typeof TableGraderExamplesRegistrySchema>;

export const EvalTableWithMetricsSchema = z.object({
  teamId: z.string(),
  datasetId: z.string().nullable(),
  table: z.object({
    head: z.object({
      prompts: z.array(z.any()), // Prompt type from @promptfoo/types
      vars: z.array(z.string()),
    }),
    body: z
      .array(z.any())
      .describe(
        'Evaluation table rows; missing provider outputs retain their column position as null, and each available result output includes its gradingRevision.',
      ), // EvaluateTableRow from @promptfoo/types
  }),
  totalCount: z.number(),
  filteredCount: z.number(),
  matchingResultIds: z
    .array(z.string())
    .optional()
    .describe(
      'IDs of the individual outputs matching the active filters on this page. Omitted when every output in the returned rows is eligible.',
    ),
  id: z.string(),
  ratingRevisions: z
    .literal(true)
    .optional()
    .describe(
      'Present when the API supports conditional individual ratings through gradingRevision and excludes stored grading-component aliases from its graderExamplesById registry. Older API deployments omit this capability; preserve their grading-component references because registry keys may collide with stored aliases.',
    ),
  filteredMetrics: z.array(PromptMetricsSchema).nullable(),
  graderExamplesById: TableGraderExamplesRegistrySchema.describe(
    'Response-scoped grader example arrays keyed by ID. Public evaluation tables omit inline grader examples, do not generate references, and always return an empty registry, even for signed-in clients; previously stored references may remain unresolved. For a private evaluation table obtained with authorized access, inline `graderExamples` arrays are generally replaced by `graderExamplesId` references. This registry is built from the returned page and response config, so its contents can vary with pagination, filtering, search, and comparison evals. Resolve generated `graderExamplesId` references only against the registry from the same response. In private evaluation table responses, grading components with an already stored ID retain that ID and any original inline examples; these stored IDs are excluded from the response registry and must not be inferred.',
  ),
  config: z.any(),
  author: z.any().nullable(),
  version: z.number(),
  isPublic: z.boolean(),
  isRedteam: z.boolean(),
  stats: EvalStatsSchema.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type EvalTableWithMetricsDTO = z.infer<typeof EvalTableWithMetricsSchema>;

const OptionalNonnegativeIntegerQueryParamSchema = z.preprocess(
  (value) => (value === '' || value === undefined ? undefined : value),
  z.coerce.number().int().min(0).optional(),
);

const OptionalPositiveIntegerQueryParamSchema = z.preprocess(
  (value) => (value === '' || value === undefined ? undefined : value),
  z.coerce.number().int().min(1).max(200).optional(),
);

export const EvalTableLocationFilterSchema = z
  .object({
    logicOperator: z.enum(['and', 'or']),
    type: z.enum([
      'metric',
      'metadata',
      'plugin',
      'strategy',
      'policy',
      'severity',
      'guardrail',
      'resultTag',
    ]),
    operator: z.enum([
      'equals',
      'not_equals',
      'contains',
      'not_contains',
      'exists',
      'is_defined',
      'eq',
      'neq',
      'gt',
      'gte',
      'lt',
      'lte',
    ]),
    value: z.string(),
    field: z.string().optional(),
  })
  .passthrough();

const SerializedEvalTableLocationFilterSchema = z.string().superRefine((value, ctx) => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    ctx.addIssue({
      code: 'custom',
      message: 'Filter must be valid JSON',
    });
    return;
  }

  const result = EvalTableLocationFilterSchema.safeParse(parsed);
  if (!result.success) {
    ctx.addIssue({
      code: 'custom',
      message: 'Filter must be a valid table filter object',
    });
  }
});

/**
 * Query parameters for resolving a stable eval table row or output link to the
 * page that contains it. The endpoint accepts either a row-level testIdx or an
 * output-level resultId, but not both.
 */
export const EvalTableLocationQuerySchema = z
  .object({
    testIdx: OptionalNonnegativeIntegerQueryParamSchema,
    resultId: z.preprocess(
      (value) => (value === '' || value === undefined ? undefined : value),
      z.string().min(1).max(512).optional(),
    ),
    limit: OptionalPositiveIntegerQueryParamSchema.prefault(50),
    mode: z
      .enum(['all', 'passes', 'failures', 'errors', 'different', 'highlights', 'grading-changes'])
      .optional()
      .prefault('all'),
    search: ResultSearchQuerySchema.optional().prefault(''),
    filter: z
      .union([
        SerializedEvalTableLocationFilterSchema,
        z.array(SerializedEvalTableLocationFilterSchema),
      ])
      .optional(),
  })
  .superRefine((value, ctx) => {
    const hasTestIdx = typeof value.testIdx === 'number';
    const hasResultId = Boolean(value.resultId);

    if (!hasTestIdx && !hasResultId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Provide testIdx or resultId',
        path: ['testIdx'],
      });
    }

    if (hasTestIdx && hasResultId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Provide only one of testIdx or resultId',
        path: ['resultId'],
      });
    }
  });

export type EvalTableLocationQuery = z.infer<typeof EvalTableLocationQuerySchema>;

export const EvalTableLocationResponseSchema = z.object({
  testIdx: z.number().int().min(0),
  resultId: z.string().optional(),
  promptIdx: z.number().int().min(0).optional(),
  offset: z.number().int().min(0),
  pageIndex: z.number().int().min(0),
  pageSize: z.number().int().min(1).max(200),
  localRowIndex: z.number().int().min(0),
});

export type EvalTableLocationResponse = z.infer<typeof EvalTableLocationResponseSchema>;

/**
 * Request schema for fetching failed test cases for retry strategy
 * Uses coerce for limit since query params are always strings
 */
export const FailedTestsRequestSchema = z.object({
  pluginId: z.string().min(1, 'Plugin ID is required'),
  targetId: z.string().min(1, 'Target ID is required'),
  // Use .min(1) instead of .positive() for OpenAPI 3.0 compatibility
  // .positive() generates exclusiveMinimum: true (draft-04) which is invalid in OpenAPI 3.0
  limit: z.coerce.number().int().min(1).max(1000).optional().prefault(100),
});

export type FailedTestsRequest = z.infer<typeof FailedTestsRequestSchema>;

/**
 * Schema for a single failed result returned by the failed-tests endpoint
 * Contains the raw data needed for retry transformation by the submodule
 */
export const FailedResultSchema = z.object({
  testCase: AtomicTestCaseSchema,
  response: ProviderResponseSchema.nullable(),
  provider: ProviderOptionsSchema,
  evalId: z.string(),
});

export type FailedResult = z.infer<typeof FailedResultSchema>;

/**
 * Type for database result from findFailedForRetry
 * Extends FailedResult with createdAt for ordering/internal use
 */
export type FailedForRetryDbResult = FailedResult & { createdAt: Date };

/**
 * Response schema for failed test cases
 * Returns raw results that the submodule transforms (handles redteamFinalPrompt extraction)
 */
export const FailedTestsResponseSchema = z.object({
  results: z.array(FailedResultSchema),
});

/**
 * Request schema for getting filtered result IDs for bulk operations
 */
export const FilteredResultIdsRequestSchema = z.object({
  filterMode: z
    .enum(['all', 'passes', 'failures', 'errors', 'different', 'highlights', 'grading-changes'])
    .optional()
    .default('all'),
  searchQuery: ResultSearchQuerySchema.optional(),
  filters: z.union([z.string(), z.array(z.string())]).optional(),
  checkSafeTransition: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
});

export type FilteredResultIdsRequest = z.infer<typeof FilteredResultIdsRequestSchema>;

/**
 * Response schema for filtered result IDs
 */
export const FilteredResultIdsResponseSchema = z.object({
  resultIds: z.array(z.string().uuid()),
  count: z.number(),
  filterDescription: z.string(),
  safeTransitionCount: z
    .int()
    .nonnegative()
    .optional()
    .describe(
      'Included when checkSafeTransition is true. Number of matching results whose verdict would change from Vulnerable to Safe.',
    ),
  requiresSafeJustification: z
    .boolean()
    .optional()
    .describe(
      'Included when checkSafeTransition is true. Indicates whether marking the matching results Safe would change any current red-team verdict from Vulnerable to Safe.',
    ),
});

export type FilteredResultIdsResponse = z.infer<typeof FilteredResultIdsResponseSchema>;

/**
 * Request schema for bulk LLM regrade
 */
export const BulkLlmRegradeRequestSchema = z.object({
  resultIds: z.array(z.string().uuid()).min(1).max(10000),
  /** Optional supplementary rubric to prepend to assertion rubrics */
  supplementaryRubric: z.string().max(10000).optional(),
});

export type BulkLlmRegradeRequest = z.infer<typeof BulkLlmRegradeRequestSchema>;

/**
 * Response schema for bulk LLM regrade (async job)
 */
export const BulkLlmRegradeResponseSchema = z.object({
  jobId: z.string().uuid(),
  statusUrl: z.string(),
});

export type BulkLlmRegradeResponse = z.infer<typeof BulkLlmRegradeResponseSchema>;

// Preserve provider-specific fields that ProviderOptionsSchema doesn't model so
// reruns receive the original stored provider object instead of a stripped one.
const LosslessProviderOptionsSchema = ProviderOptionsSchema.catchall(z.unknown());

/**
 * Schema for a single failed test case.
 * Used by the "rerun failed tests" functionality.
 *
 * Preserves the rerun-critical known fields while retaining unknown keys from
 * stored test cases so reruns stay faithful to the original configuration.
 */
export const FailedTestCaseSchema = z
  .object({
    vars: z.record(z.string(), z.unknown()).optional(),
    provider: z.union([z.string(), LosslessProviderOptionsSchema]).optional(),
    providers: z.array(z.string()).optional(),
    prompts: z.array(z.string()).optional(),
    providerOutput: z.union([z.string(), z.record(z.string(), z.unknown())]).optional(),
    assert: z.array(z.unknown()).optional(),
    assertScoringFunction: z.string().optional(),
    threshold: z.number().optional(),
    description: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    options: z.unknown().optional(),
  })
  .catchall(z.unknown());

export type FailedTestCase = z.infer<typeof FailedTestCaseSchema>;

/**
 * Response schema for getFailedTestCases endpoint.
 * Returns all unique failed test cases for an evaluation without pagination.
 */
export const FailedTestCasesResponseSchema = z.object({
  testCases: z.array(FailedTestCaseSchema).describe('Array of unique failed test cases'),
  count: z.number().int().nonnegative().describe('Number of failed test cases returned'),
});

export type FailedTestCasesResponse = z.infer<typeof FailedTestCasesResponseSchema>;
